import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Music4 } from "lucide-react";
import { MaskLine, EASE } from "./motion";
import { IMAGES } from "./content";

const Blob = ({ color, className, delay = 0, size }) => (
  <motion.span
    aria-hidden
    className={`absolute rounded-full blur-[2px] ${className}`}
    style={{ backgroundColor: color, width: size, height: size }}
    animate={{ y: [0, -22, 0], x: [0, 10, 0] }}
    transition={{ duration: 6 + delay, repeat: Infinity, ease: "easeInOut", delay }}
  />
);

export const Hero = ({ onContact }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pt-32 md:pt-40">
      {/* Ambient gradient wash */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-0 h-[520px] w-[520px] rounded-full bg-dm-guita/20 blur-[120px]" />
        <div className="absolute right-0 top-40 h-[420px] w-[420px] rounded-full bg-dm-vox/20 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-[360px] w-[360px] rounded-full bg-dm-keys/20 blur-[120px]" />
      </div>

      <div className="mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-10 px-5 md:px-8 lg:grid-cols-12">
        {/* Left: kinetic headline */}
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-dm-ink/10 bg-white/60 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-dm-ink backdrop-blur"
          >
            <Music4 className="h-4 w-4 text-dm-guita" /> Marca infantil 360º
          </motion.div>

          <h1 className="font-display text-[3.1rem] font-black leading-[0.92] tracking-tighter text-dm-ink sm:text-6xl lg:text-[5.6rem]">
            <MaskLine delay={0.15}>Onde a música</MaskLine>
            <MaskLine delay={0.28}>
              vira <span className="dm-rainbow-text">amizade</span>
            </MaskLine>
            <MaskLine delay={0.41}>e a imaginação</MaskLine>
            <MaskLine delay={0.54}>vira brincadeira.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-dm-muted md:text-xl"
          >
            A DóRéMigos troca o consumo passivo de vídeos por uma experiência{" "}
            <span className="font-semibold text-dm-ink">ativa e sensorial</span> — humanizando o
            ensino musical e apoiando o desenvolvimento real das crianças.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={onContact}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-dm-ink px-7 py-4 text-base font-semibold text-dm-cream transition-transform duration-300 hover:scale-[1.04]"
            >
              Quero licenciar
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              href="#pilares"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full border border-dm-ink/15 bg-white/50 px-7 py-4 text-base font-semibold text-dm-ink backdrop-blur transition-colors duration-300 hover:bg-white"
            >
              Ver o ecossistema
            </a>
          </motion.div>
        </motion.div>

        {/* Right: parallax hero image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: EASE }}
          className="relative lg:col-span-5"
        >
          <Blob color="#FFD600" className="-left-6 top-6 z-0" size="86px" delay={0.5} />
          <Blob color="#00E676" className="-right-4 top-1/3 z-0" size="64px" delay={1.4} />
          <Blob color="#9D4EDD" className="bottom-8 left-4 z-0" size="52px" delay={0.9} />

          <div className="relative z-10 overflow-hidden rounded-[2.5rem] border-4 border-white shadow-[0_30px_80px_rgba(0,0,0,0.14)]">
            <motion.img
              src={IMAGES.heroGang}
              alt="A turma DóRéMigos: seis amigos, seis instrumentos"
              style={{ y: imgY, scale: imgScale }}
              className="h-full w-full object-cover"
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1, ease: EASE }}
            className="absolute -bottom-5 left-6 z-20 flex items-center gap-3 rounded-2xl bg-dm-ink px-5 py-3 text-dm-cream shadow-xl"
          >
            <span className="font-display text-2xl font-black text-dm-keys">6</span>
            <span className="text-sm font-medium leading-tight">
              amigos,<br />6 instrumentos
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
